<template>
  <SmDialog
    v-model:visibility="visibility"
    :title="i18n.t('datev.newImport')"
    :size="dialogSize">
    <div class="flex flex-col gap-6">
      <template v-if="currentStep === 'upload'">
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-contrast">
              {{ i18n.t('name') }}
            </span>
            <SmInput
              v-model="form.name"
              class="w-full"
              size="medium"
              design="form"
              outline
              :disabled="uploadStatus === 'loading'"
              :label="i18n.t('datev.nameExample')" />
          </div>

          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-contrast">
              {{ i18n.t('description') }}
            </span>
            <SmInput
              v-model="form.description"
              class="w-full"
              size="medium"
              design="form"
              outline
              :disabled="uploadStatus === 'loading'"
              :label="i18n.t('datev.descriptionExample')" />
          </div>

          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-contrast">
              {{ i18n.t('date') }}
            </span>
            <SmDatepicker
              v-model:date="form.date"
              class="mt-1 w-full"
              :disabled="uploadStatus === 'loading'"
              :placeholder="i18n.t('pleaseSelectDate')"
              :min-date="null" />
          </div>

          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-contrast">
              {{ i18n.t('tags') }}
            </span>
            <SmSelect
              v-model:selection="selectedTagOptions"
              class="w-full"
              :options="tagOptions"
              :label="i18n.t('chooseTags')"
              :disabled="uploadStatus === 'loading'"
              :multiselect="true"
              searchable
              trigger-style="default">
              <template #item="{ item }">
                <div class="flex w-full items-center">
                  <TagComponent
                    v-if="tags[item.value]"
                    :tag="tags[item.value]" />
                  <span v-else>{{ item.label }}</span>
                </div>
              </template>
            </SmSelect>
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <el-upload
            ref="uploadRef"
            v-model:file-list="fileList"
            :class="['w-full rounded-lg', { hidden: fileList.length > 0 }]"
            :auto-upload="false"
            :on-success="handleUploadSuccess"
            :on-error="handleUploadError"
            :on-progress="handleUploadProgress"
            :on-change="handleChange"
            :limit="1"
            drag
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            :show-file-list="false"
            :action="`${OpenAPI.BASE}/api/v1/accounting/accounting-imports`"
            :headers="{
              Authorization: `Bearer ${OpenAPI.TOKEN}`,
            }"
            :data="uploadData">
            <template #default>
              <div
                v-if="fileList.length === 0"
                class="flex flex-col items-center">
                <CloudUpload :size="30" class="mb-2 text-contrast" />
                <p>
                  {{ i18n.t('datev.uploadHint') }}
                </p>
                <p class="text-sm text-contrast-muted">
                  {{ i18n.t('datevMaxFilesizeHint') }}
                </p>
              </div>
            </template>
          </el-upload>

          <p v-if="fileValidationError" class="mt-2 text-sm text-red">
            {{ i18n.t('error.import.format.invalid_file_format.message') }}
          </p>

          <div
            v-if="fileList.length > 0"
            class="flex items-center justify-between rounded border border-gray-300 px-4 py-3 dark:border-gray-600">
            <div class="flex flex-col">
              <span class="text-sm font-medium text-contrast">
                {{ fileList[0]?.name }}
              </span>
              <span class="text-xs text-contrast-muted">
                {{ Math.round((fileList[0]?.size || 0) / 1024) }} KB
              </span>
            </div>
            <SmButton size="small" outline @click="removeFile">
              {{ i18n.t('general.remove') }}
            </SmButton>
          </div>
        </div>
      </template>

      <template v-else>
        <AccountingImportReviewStep
          :config-draft="configDraft"
          :currency-options="currencyOptions"
          v-model:currency-selection="currencySelection"
          :autodetect-label="autodetectLabel"
          :processing-status="processingStatus"
          :validate-status="validateStatus"
          :validate-error-message="validateErrorMessage"
          :validation-samples="validationSamples"
          @set-column-field="setColumnField"
          @set-number-field="setNumberField"
          @set-decimal-separator="setDecimalSeparator"
          @toggle-ai="setAiPrediction" />
      </template>
    </div>
    <template #footer>
      <div v-if="currentStep === 'upload'" class="flex gap-2">
        <SmButton
          outline
          :disabled="uploadStatus === 'loading'"
          @click="handleCancel">
          {{ i18n.t('cancel') }}
        </SmButton>
        <SmButton
          type="primary"
          :loading="uploadStatus === 'loading'"
          :disabled="!fileList.length || uploadStatus === 'loading'"
          @click="upload">
          {{ i18n.t('upload') }}
        </SmButton>
      </div>
      <div v-else class="flex flex-wrap items-center justify-between gap-2">
        <div class="flex gap-2">
          <SmButton
            outline
            :disabled="processingStatus === 'loading'"
            @click="backToUpload">
            {{ i18n.t('back') }}
          </SmButton>
        </div>
        <div class="flex flex-wrap gap-2">
          <SmButton
            v-if="showValidateButton"
            type="primary"
            :loading="validateStatus === 'loading'"
            :disabled="processingStatus === 'loading'"
            @click="revalidate">
            {{ i18n.t('accountingExport.saveAndValidate') }}
          </SmButton>
          <SmButton
            v-if="showStartProcessButton"
            type="primary"
            :loading="processingStatus === 'loading'"
            :disabled="processingStatus === 'loading'"
            @click="startProcessing">
            {{ i18n.t('accountingExport.startProcessing') }}
          </SmButton>
        </div>
      </div>
    </template>
  </SmDialog>
</template>

<script lang="ts" setup>
  import { computed, onMounted, ref, watch } from 'vue'
  import { UploadInstance, UploadUserFile, UploadFile } from 'element-plus'
  import { useI18n } from 'vue-i18n'

  import {
    AccountingImportConfigUpdate,
    AccountingImportOut,
    AccountingImportRowSample,
    AccountingService,
    Currency,
    CurrencyCode,
    CurrencyService,
    DecimalSeparator,
    OpenAPI,
    Tag,
    TagsService,
    ApiError,
  } from '@/client'
  import { sendToast } from '@/components/sm/SmNotification'
  import SmSelect, { Option } from '@/components/sm/SmSelect.vue'
  import SmDatepicker from '@/components/sm/SmDatepicker.vue'
  import TagComponent from '@/components/tags/TagComponent.vue'
  import SmButton from '@/components/sm/SmButton.vue'
  import SmInput from '@/components/sm/SmInput.vue'
  import { sortCurrencies } from '@/utils/currency/currencyUtil'
  import { CloudUpload } from 'lucide-vue-next'
  import AccountingImportReviewStep from './AccountingImportReviewStep.vue'
  import { getApiErrorMessage } from '@/utils/errors'

  const i18n = useI18n()

  const uploadStatus = ref<'idle' | 'loading' | 'success' | 'error'>('idle')
  const validateStatus = ref<'idle' | 'loading' | 'success' | 'error'>('idle')
  const validateErrorMessage = ref<string | null>(null)
  const processingStatus = ref<'idle' | 'loading' | 'error'>('idle')
  const currentStep = ref<'upload' | 'review'>('upload')
  const currentImportId = ref<string | null>(null)
  const processingStarted = ref(false)
  const isReanalyzeMode = ref(false)

  const uploadRef = ref<UploadInstance>()
  const fileList = ref<UploadUserFile[]>([])
  const availableTags = ref<Tag[]>([])
  const validationSamples = ref<AccountingImportRowSample[]>([])
  const autodetectLabel = computed(() => {
    const value = i18n.t('autodetect')
    return value || 'Autodetect'
  })
  const currencies = ref<Currency[]>([])
  const currencyOptions = computed<Option<string>[]>(() =>
    currencies.value.map((currency) => ({
      label: currency._id,
      value: currency._id,
      key: currency._id,
    }))
  )
  const currencySelection = computed<Option<string>[] | undefined>({
    get() {
      if (!configDraft.value.currency_code) return []
      const match = currencyOptions.value.find(
        (option) => option.value === configDraft.value.currency_code
      )
      return match ? [match] : []
    },
    set(value) {
      const first = value?.[0]
      setCurrencyCode(first?.value ?? '')
    },
  })
  const configDraft = ref<AccountingImportConfigUpdate>({
    enable_ai_prediction: false,
  })
  const lastValidatedConfig = ref<AccountingImportConfigUpdate | null>(null)

  const props = defineProps<{
    visibility: boolean
    reanalyzeImportId?: string | null
  }>()

  const emit = defineEmits<{
    (event: 'update:visibility', value: boolean): void
    (event: 'imported'): void
    (event: 'closed'): void
  }>()

  const visibility = computed({
    get() {
      return props.visibility
    },
    set(value) {
      emit('update:visibility', value)
    },
  })

  function initializeForm() {
    return {
      name: '',
      description: '',
      date: new Date() as Date | undefined,
      tags: [] as string[],
      config: {
        enable_ai_prediction: false,
      } as AccountingImportConfigUpdate,
    }
  }

  const form = ref(initializeForm())

  function timeStampToUnix(timeStamp?: number | Date) {
    if (!timeStamp) return undefined

    if (typeof timeStamp === 'number') return timeStamp

    return timeStamp.toISOString()
  }

  const tags = computed<Record<string, Tag>>(() =>
    availableTags.value.reduce(
      (acc, tag) => {
        acc[tag._id] = tag
        return acc
      },
      {} as Record<string, Tag>
    )
  )

  const uploadData = computed(() => {
    const formattedDate = timeStampToUnix(form.value.date)

    const data: Record<string, any> = {
      name: form.value.name,
      description: form.value.description,
      filename: fileList.value[0]?.name,
      config: JSON.stringify(form.value.config),
    }

    if (formattedDate) {
      data.date = formattedDate
    }
    return data
  })

  const tagOptions = computed<Option<string>[]>(() =>
    availableTags.value.map((tag) => ({
      label: tag.name,
      value: tag._id,
      key: tag._id,
    }))
  )

  const selectedTagOptions = computed<Option<string>[]>({
    get() {
      return tagOptions.value.filter((option) =>
        form.value.tags.includes(option.value)
      )
    },
    set(value) {
      form.value.tags = value.map((option) => option.value)
    },
  })

  const dialogSize = computed(() =>
    currentStep.value === 'review' ? 'large' : 'medium'
  )

  const configChangedSinceValidation = computed(() => {
    return (
      JSON.stringify(configDraft.value ?? {}) !==
      JSON.stringify(lastValidatedConfig.value ?? {})
    )
  })

  const hasRequiredColumnError = computed(() => {
    return (
      validateStatus.value === 'error' &&
      validateErrorMessage.value ===
        i18n.t('accountingExport.requiredColumnMissing')
    )
  })

  const hasInvalidUploadError = computed(() => {
    return (
      validateStatus.value === 'error' &&
      validateErrorMessage.value === i18n.t('datev.invalidUpload')
    )
  })

  const showValidateButton = computed(() => {
    if (hasInvalidUploadError.value) return false
    return configChangedSinceValidation.value || hasRequiredColumnError.value
  })

  const showStartProcessButton = computed(() => {
    if (hasInvalidUploadError.value) return false
    return (
      validateStatus.value === 'success' && !configChangedSinceValidation.value
    )
  })

  function setColumnField(
    key:
      | 'description_column'
      | 'cost_column'
      | 'currency_column'
      | 'booking_date_column',
    value: string
  ) {
    configDraft.value[key] = value ? value : null
  }

  function setDecimalSeparator(value: string) {
    configDraft.value.cost_decimal_separator = value
      ? (value as DecimalSeparator)
      : undefined
  }

  function setCurrencyCode(value: string) {
    configDraft.value.currency_code = value
      ? (value as CurrencyCode)
      : undefined
  }

  function setNumberField(
    key: 'skip_rows' | 'prediction_threshold',
    value: number | string
  ) {
    if (value === undefined || value === null || value === '') {
      configDraft.value[key] = null
      return
    }
    const parsed = typeof value === 'number' ? value : Number(value)
    configDraft.value[key] = Number.isNaN(parsed) ? undefined : parsed
  }

  function setAiPrediction(value: boolean) {
    configDraft.value.enable_ai_prediction = value
  }

  async function loadTags() {
    try {
      const tagsResponse = await TagsService.getTagsApiV1ManagementTagsGet({})
      availableTags.value = tagsResponse
    } catch (error) {
      console.error(error)
    }
  }

  onMounted(() => {
    loadTags()
    loadCurrencies()
  })

  async function loadCurrencies() {
    try {
      const data = await CurrencyService.getCurrenciesCurrenciesGet({})
      currencies.value = sortCurrencies(data ?? [])
    } catch (error) {
      console.error(error)
    }
  }

  const fileValidationError = ref(false)
  const allowedExtensions = ['csv', 'xlsx']

  function isValidFile(file: UploadUserFile): boolean {
    if (!file.name) return false
    const extension = file.name.split('.').pop()?.toLowerCase()
    return extension ? allowedExtensions.includes(extension) : false
  }

  function handleChange(file: UploadFile) {
    if (file.status === 'ready') {
      if (!isValidFile(file as UploadUserFile)) {
        fileValidationError.value = true
        sendToast(
          i18n.t('error.import.format.invalid_file_format.message'),
          undefined,
          'error'
        )

        uploadRef.value?.handleRemove(file)
        fileList.value = []
      } else {
        fileValidationError.value = false
        fileList.value = [file as UploadUserFile]
      }
    }
  }

  watch(visibility, async function handleVisibilityChange(value: boolean) {
    if (!value) {
      emit('closed')
      cleanupAndReset()
    } else {
      validateStatus.value = 'idle'
      validateErrorMessage.value = null
      validationSamples.value = []
      if (availableTags.value.length === 0) {
        loadTags()
      }
      if (props.reanalyzeImportId) {
        await prepareReanalysis(props.reanalyzeImportId)
      }
    }
  })

  function upload() {
    if (!fileList.value.length || uploadStatus.value === 'loading') return

    if (!isValidFile(fileList.value[0])) {
      sendToast(
        i18n.t('error.import.format.invalid_file_format.message'),
        undefined,
        'error'
      )
      return
    }

    uploadStatus.value = 'loading'
    uploadRef.value?.submit()
  }

  async function handleUploadSuccess(response: AccountingImportOut) {
    let importId: string | undefined

    try {
      importId =
        response?._id ??
        (response as unknown as { id?: string })?.id ??
        (response as unknown as { data?: { _id?: string } })?.data?._id

      if (!importId) {
        uploadStatus.value = 'error'
        sendToast(i18n.t('uploadError'), undefined, 'error')
        return
      }

      currentImportId.value = importId
      configDraft.value = { ...form.value.config }

      uploadStatus.value = 'success'
      sendToast(i18n.t('datev.uploadSuccess'), undefined, 'success')

      await validateImport(importId)
    } catch (error) {
      console.error(error)
      sendToast(i18n.t('uploadError'), undefined, 'error')
      uploadStatus.value = 'error'
    }
  }

  function handleUploadError() {
    uploadStatus.value = 'error'
  }

  function handleUploadProgress() {
    uploadStatus.value = 'loading'
  }

  async function validateImport(importId: string) {
    validateStatus.value = 'loading'
    validateErrorMessage.value = null
    try {
      const response =
        await AccountingService.validateAccountingImportApiV1AccountingAccountingImportsAccountingImportIdValidatePost(
          {
            accountingImportId: importId,
            requestBody: buildValidationRequestBody(),
          }
        )
      const samples = Array.isArray(response)
        ? response
        : ((response as any)?.accounting_import_row_samples ??
          (response as any)?.samples)
      validationSamples.value =
        (samples as AccountingImportRowSample[])?.slice() ?? []
      const updatedConfig = (response as any)?.accounting_import_config as
        | AccountingImportConfigUpdate
        | undefined
      if (updatedConfig) {
        configDraft.value = { ...updatedConfig }
        lastValidatedConfig.value = JSON.parse(
          JSON.stringify(updatedConfig)
        ) as AccountingImportConfigUpdate
      } else {
        lastValidatedConfig.value = JSON.parse(
          JSON.stringify(configDraft.value)
        ) as AccountingImportConfigUpdate
      }
      validateStatus.value = 'success'
      currentStep.value = 'review'
    } catch (error) {
      handleValidationError(error)
      validationSamples.value = []
      currentStep.value = 'review'
    }
  }

  async function prepareReanalysis(importId: string) {
    try {
      isReanalyzeMode.value = true
      currentImportId.value = importId
      uploadStatus.value = 'success'
      processingStarted.value = false
      currentStep.value = 'review'
      await fetchImportConfig(importId)
      await validateImport(importId)
    } catch (error) {
      console.error(error)
      sendToast(i18n.t('failedToLoadData'), undefined, 'error')
      visibility.value = false
    }
  }

  async function revalidate() {
    if (!currentImportId.value) return
    if (validateStatus.value === 'loading') return

    try {
      validateStatus.value = 'loading'
      validateErrorMessage.value = null
      await validateImport(currentImportId.value)
    } catch (error) {
      handleValidationError(error)
    }
  }

  function buildValidationRequestBody() {
    const normalizedConfig: AccountingImportConfigUpdate = {
      ...configDraft.value,
    }
    if (
      normalizedConfig.skip_rows === null ||
      normalizedConfig.skip_rows === undefined
    ) {
      normalizedConfig.skip_rows = 0
    }

    return {
      accounting_import_config: normalizedConfig,
      save_config_on_success: true,
    }
  }

  async function fetchImportConfig(importId?: string) {
    const id = importId ?? currentImportId.value
    if (!id) return
    try {
      const record =
        await AccountingService.getAccountingImportApiV1AccountingAccountingImportsAccountingImportIdGet(
          { accountingImportId: id }
        )
      if (record?.config) {
        configDraft.value = { ...record.config }
        lastValidatedConfig.value = JSON.parse(
          JSON.stringify(configDraft.value)
        )
      }
      form.value.name = record?.name ?? form.value.name
      form.value.description = record?.description ?? form.value.description
      form.value.date = record?.date ? new Date(record.date) : form.value.date
      form.value.tags = record?.tags ?? form.value.tags
      currentImportId.value = id
    } catch (error) {
      console.error(error)
      sendToast(i18n.t('failedToLoadData'), undefined, 'error')
    }
  }

  function handleValidationError(error: unknown) {
    console.error(error)
    validateStatus.value = 'error'
    const message =
      error instanceof ApiError
        ? getApiErrorMessage(
            error,
            i18n as ReturnType<typeof useI18n>,
            i18n.t('failedToLoadData')
          )
        : i18n.t('failedToLoadData')
    const finalMessage =
      message === i18n.t('failedToLoadData')
        ? i18n.t('datev.invalidUpload')
        : message
    validateErrorMessage.value = finalMessage
    sendToast(finalMessage, undefined, 'error')
  }

  async function startProcessing() {
    if (!currentImportId.value || configChangedSinceValidation.value) return
    if (processingStatus.value === 'loading') return

    processingStatus.value = 'loading'
    try {
      await AccountingService.startProcessingAccountingImportApiV1AccountingAccountingImportsAccountingImportIdStartProcessingPost(
        {
          accountingImportId: currentImportId.value,
        }
      )
      processingStarted.value = true
      sendToast(i18n.t('datev.uploadSuccess'), undefined, 'success')
      emit('imported')
      visibility.value = false
    } catch (error) {
      console.error(error)
      processingStatus.value = 'error'
      sendToast(i18n.t('uploadError'), undefined, 'error')
    } finally {
      processingStatus.value = 'idle'
    }
  }

  async function backToUpload() {
    removeFile()
    currentStep.value = 'upload'
    validateStatus.value = 'idle'
    validationSamples.value = []
    lastValidatedConfig.value = null
  }

  function removeFile() {
    uploadRef.value?.clearFiles()
    fileList.value = []
    uploadStatus.value = 'idle'
    fileValidationError.value = false
  }

  async function cleanupAndReset() {
    currentImportId.value = null
    form.value = initializeForm()
    configDraft.value = { enable_ai_prediction: false }
    fileList.value = []
    uploadStatus.value = 'idle'
    validateStatus.value = 'idle'
    validateErrorMessage.value = null
    processingStatus.value = 'idle'
    fileValidationError.value = false
    currentStep.value = 'upload'
    validationSamples.value = []
    lastValidatedConfig.value = null
    processingStarted.value = false
    isReanalyzeMode.value = false
    if (uploadRef.value) {
      uploadRef.value.clearFiles()
    }
  }

  function handleCancel() {
    visibility.value = false
  }
</script>

<style scoped>
  ::v-deep(.el-upload-dragger) {
    border-radius: 10px;
  }
</style>
